import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/config";
import { toast } from "sonner";

const TIERS = [
  { value: "mid-range", label: "Mid-range", desc: "Comfortable lodges & quality tented camps" },
  { value: "luxury", label: "Luxury", desc: "Premium tented camps & boutique lodges" },
  { value: "premium", label: "Premium", desc: "All-suite lodges, fly-in transfers, exclusive use" },
];

export default function BookingPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const tourSlug = params.get("tour");

  const [tours, setTours] = useState([]);
  const [tour, setTour] = useState(null);
  const [form, setForm] = useState({
    tour_slug: tourSlug || "",
    full_name: "",
    email: "",
    phone: "",
    country: "",
    start_date: "",
    travelers: 2,
    accommodation_tier: "mid-range",
    special_requests: "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/tours").then((r) => setTours(r.data.tours || []));
  }, []);

  useEffect(() => {
    if (form.tour_slug) {
      const t = tours.find((x) => x.slug === form.tour_slug);
      setTour(t || null);
    }
  }, [form.tour_slug, tours]);

  const tier = TIERS.find((t) => t.value === form.accommodation_tier);
  const onChange = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.tour_slug) {
      toast.error("Please select a tour");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post("/bookings", form);
      const booking = res.data.booking;
      toast.success(`Booking received! Reference ${booking.booking_reference}`);
      navigate(`/booking-success?booking_id=${booking.id}`);
    } catch (err) {
      const msg = err?.response?.data?.detail?.[0]?.msg || "Something went wrong. Please try WhatsApp instead.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="booking-page" className="pt-36 md:pt-40 pb-24 bg-[#FAF6F0]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center max-w-3xl mx-auto">
          <p className="overline">booking</p>
          <h1 className="font-display text-5xl md:text-7xl italic font-light mt-4 leading-tight">Reserve Your Tanzania Experience</h1>
          <p className="mt-5 text-[#23170E]/65">
            Fill in the form below — no payment is taken online. We'll respond within 24 hours with a personalized itinerary and quote.
          </p>
        </div>

        <form onSubmit={submit} className="mt-14 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-7 bg-[#FFFFFF] rounded-3xl p-8 md:p-10 border border-[#E2D5C3] space-y-7">
            <div>
              <label className="overline">Tour</label>
              <select
                data-testid="booking-tour-select"
                value={form.tour_slug}
                onChange={(e) => onChange("tour_slug", e.target.value)}
                required
                className="zinput mt-3"
              >
                <option value="">— Select a tour —</option>
                {tours.map((t) => (
                  <option key={t.slug} value={t.slug}>{t.title} ({t.duration_label})</option>
                ))}
              </select>
            </div>

            <div className="grid sm:grid-cols-2 gap-6">
              <Field label="Full name" required>
                <input data-testid="booking-full-name" type="text" required value={form.full_name} onChange={(e) => onChange("full_name", e.target.value)} className="zinput" />
              </Field>
              <Field label="Email" required>
                <input data-testid="booking-email" type="email" required value={form.email} onChange={(e) => onChange("email", e.target.value)} className="zinput" />
              </Field>
              <Field label="Phone / WhatsApp" required>
                <input data-testid="booking-phone" type="tel" required value={form.phone} onChange={(e) => onChange("phone", e.target.value)} className="zinput" />
              </Field>
              <Field label="Country">
                <input data-testid="booking-country" type="text" value={form.country} onChange={(e) => onChange("country", e.target.value)} className="zinput" />
              </Field>
              <Field label="Preferred start date" required>
                <input data-testid="booking-date" type="date" required value={form.start_date} onChange={(e) => onChange("start_date", e.target.value)} className="zinput" />
              </Field>
              <Field label="Number of travelers" required>
                <input data-testid="booking-travelers" type="number" min={1} max={20} required value={form.travelers} onChange={(e) => onChange("travelers", parseInt(e.target.value || "1"))} className="zinput" />
              </Field>
            </div>

            <div>
              <label className="overline">Accommodation preference</label>
              <div className="mt-4 space-y-3">
                {TIERS.map((t) => (
                  <label
                    key={t.value}
                    data-testid={`tier-${t.value}`}
                    className={`flex items-start gap-4 p-5 rounded-2xl border cursor-pointer transition-colors ${
                      form.accommodation_tier === t.value ? "border-[#A6521B] bg-[#A6521B]/8" : "border-[#E2D5C3] bg-[#FAF6F0]/40 hover:border-[#23170E]/30"
                    }`}
                  >
                    <input
                      type="radio"
                      name="tier"
                      value={t.value}
                      checked={form.accommodation_tier === t.value}
                      onChange={(e) => onChange("accommodation_tier", e.target.value)}
                      className="mt-1.5 accent-[#A6521B]"
                    />
                    <div className="flex-1">
                      <span className="font-marcellus text-xl">{t.label}</span>
                      <p className="text-sm text-[#23170E]/65 mt-1">{t.desc}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <Field label="Special requests (dietary, accessibility, celebrations…)">
              <textarea data-testid="booking-requests" rows={4} value={form.special_requests} onChange={(e) => onChange("special_requests", e.target.value)} className="zinput resize-none" />
            </Field>
          </div>

          <aside className="md:col-span-5 md:col-start-9">
            <div className="sticky top-28 bg-[#1F150E] text-[#FAF6F0] rounded-3xl p-8 md:p-10">
              <p className="overline" style={{ color: "#C88B35" }}>summary</p>
              {tour ? (
                <>
                  <h3 className="font-marcellus text-2xl mt-3 leading-tight text-[#FAF6F0]">{tour.title}</h3>
                  <p className="text-sm text-[#FAF6F0]/65 mt-1">{tour.subtitle || tour.duration_label}</p>

                  <div className="mt-6 pt-6 border-t border-[#FAF6F0]/15 space-y-3 text-sm">
                    <Row k="Duration" v={tour.duration_label} />
                    <Row k="Destinations" v={tour.park} />
                    <Row k="Accommodation" v={tier.label} />
                    <Row k="Travelers" v={form.travelers} />
                    {form.start_date && <Row k="Start date" v={form.start_date} />}
                  </div>
                </>
              ) : (
                <p className="text-sm text-[#FAF6F0]/65 mt-4">Select a tour to see your booking summary.</p>
              )}

              <button
                type="submit"
                data-testid="booking-submit"
                disabled={submitting}
                className="mt-8 w-full btn-pill btn-pill-primary justify-center"
              >
                {submitting ? "Submitting…" : "Book Now"}
              </button>
              <p className="text-[10px] text-[#FAF6F0]/50 mt-4 text-center uppercase tracking-[0.18em]">
                No payment now · Quote sent within 24 hours
              </p>
            </div>
          </aside>
        </form>
      </div>

      <style>{`
        .zinput { width: 100%; background: #FAF6F0; border: 1px solid rgba(58, 39, 24, 0.18); border-radius: 9999px; padding: 12px 20px; color: #23170E; outline: none; transition: border-color 200ms; font-family: 'Manrope', sans-serif; font-size: 0.95rem; }
        .zinput:focus { border-color: #A6521B; }
        textarea.zinput { border-radius: 24px; padding: 14px 20px; }
        select.zinput { appearance: none; background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'><path fill='%233A2718' d='M6 8L0 0h12z'/></svg>"); background-repeat: no-repeat; background-position: right 20px center; padding-right: 48px; }
      `}</style>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="overline">{label}{required && " *"}</span>
      <div className="mt-3">{children}</div>
    </label>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between gap-4 text-[#FAF6F0]/90">
      <span className="text-[#FAF6F0]/65">{k}</span>
      <span className="text-right">{v}</span>
    </div>
  );
}
